import {
  type NotificationChannelItem,
  type VerifyNotificationChannelResponse,
} from '@utazzo/shared';
import { UzBadge, UzButton, UzDrawer, UzInput } from '@utazzo/ui';
import { AlertCircle, CheckCircle2, Plug } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import {
  useNotificationChannelConfig,
  useUpdateNotificationChannelConfig,
  useVerifyNotificationChannel,
} from '../use-notifications';

interface ChannelConfigDrawerProps {
  channel: NotificationChannelItem;
  onClose: () => void;
}

/**
 * Per-channel secrets editor (ADR-0049). For the `email` channel this
 * surfaces the `email.smtp.*` keys from the SecretsManager so the
 * operator can rotate creds, save, and "Test connection" without
 * leaving the notifications module.
 *
 * The `whatsapp` channel surfaces its DoubleTick keys the same way. Its
 * "Test connection" checks DoubleTick's channel-profile endpoint, so a
 * pass means the key is valid and the API answered; template approval
 * is still only proven by a real send. The result card says so.
 *
 * Stub channels (sms/push) render the empty fields list with a "no
 * config required yet" hint until their implementation lands.
 */
export const ChannelConfigDrawer = ({
  channel,
  onClose,
}: ChannelConfigDrawerProps): React.JSX.Element => {
  const configQuery = useNotificationChannelConfig(channel.code);
  const update = useUpdateNotificationChannelConfig();
  const verify = useVerifyNotificationChannel();

  const [draft, setDraft] = useState<Record<string, string>>({});
  const [verifyResult, setVerifyResult] = useState<VerifyNotificationChannelResponse | null>(null);

  // Hydrate the draft when the server response lands. We DON'T copy
  // sensitive masked values (`***`) into the draft; an empty string
  // means "no change" on save.
  useEffect(() => {
    if (!configQuery.data) return;
    const seed: Record<string, string> = {};
    for (const f of configQuery.data.fields) {
      seed[f.key] = f.sensitive ? '' : (f.value ?? '');
    }
    setDraft(seed);
  }, [configQuery.data]);

  const fields = configQuery.data?.fields ?? [];
  const dirty = (key: string, original: string | null, sensitive: boolean): boolean => {
    const current = draft[key] ?? '';
    if (sensitive) return current.length > 0;
    return current !== (original ?? '');
  };

  const save = (): void => {
    const changed = fields
      .filter((f) => dirty(f.key, f.value, f.sensitive))
      .map((f) => ({ key: f.key, value: draft[f.key] ?? '' }));
    if (changed.length === 0) return;
    update.mutate(
      { code: channel.code, payload: { fields: changed } },
      {
        onSuccess: () => {
          setVerifyResult(null);
          configQuery.refetch();
        },
      },
    );
  };

  const runVerify = (): void => {
    verify.mutate(channel.code, { onSuccess: (r) => setVerifyResult(r) });
  };

  return (
    <UzDrawer
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Configure ${channel.name}`}
      description={
        channel.kind === 'EMAIL'
          ? 'SMTP credentials and From address. Stored encrypted in the Secrets manager; rotated values take effect on the next send.'
          : channel.kind === 'WHATSAPP'
            ? 'DoubleTick API credentials. Stored encrypted in the Secrets manager; rotated values take effect on the next send. Message wording lives in the Meta-approved templates - this only connects us to DoubleTick.'
            : `Channel config for ${channel.code}.`
      }
      contentClassName="sm:w-[640px]! sm:max-w-none!"
      footer={
        <div className="flex justify-between gap-2">
          <UzButton
            variant="outline"
            onClick={runVerify}
            disabled={verify.isPending || configQuery.isLoading}
          >
            <Plug className="mr-1.5 h-4 w-4" />
            Test connection
          </UzButton>
          <div className="flex gap-2">
            <UzButton variant="ghost" onClick={onClose}>
              Close
            </UzButton>
            <UzButton onClick={save} disabled={update.isPending || configQuery.isLoading}>
              Save
            </UzButton>
          </div>
        </div>
      }
    >
      {configQuery.isLoading ? (
        <div className="px-1 py-4 text-sm text-zinc-500">Loading...</div>
      ) : fields.length === 0 ? (
        <div className="rounded-md border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">
          <p>No config keys defined for this channel yet.</p>
          <p className="mt-1 text-xs text-zinc-500">
            Channel kind: <span className="font-mono">{channel.kind}</span>
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {fields.map((f) => (
            <div key={f.key} className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-zinc-800">{f.label}</label>
                {f.sensitive && (
                  <UzBadge tone="warning" pill>
                    Sensitive
                  </UzBadge>
                )}
                {/* Sensitive fields need this MORE than plain ones, not
                    less. Their value never comes back to the browser, so
                    the box is always empty on reopen - without a badge
                    there is no way to tell "saved, hidden" from "never
                    saved", and the natural reading of an empty box is
                    that the save was lost. */}
                {f.present ? (
                  <UzBadge tone="success" pill>
                    Saved
                  </UzBadge>
                ) : (
                  <UzBadge tone="secondary" pill>
                    Not set
                  </UzBadge>
                )}
              </div>
              <UzInput
                type={f.sensitive ? 'password' : 'text'}
                value={draft[f.key] ?? ''}
                onChange={(e) => setDraft((prev) => ({ ...prev, [f.key]: e.target.value }))}
                placeholder={
                  f.sensitive
                    ? f.present
                      ? 'Saved and hidden - type here only to replace it'
                      : 'Paste the key here, then Save'
                    : ''
                }
                disabled={update.isPending}
              />
              <p className="text-[11px] text-zinc-500">
                <span className="font-mono">{f.key}</span>
                {f.hint && <> - {f.hint}</>}
              </p>
            </div>
          ))}
          {/* A save that never reached the server used to be completely
              silent - the drawer just sat there, and because sensitive
              fields always reopen blank, the operator's only evidence
              was an empty box that looks identical to success. Say it
              out loud instead. */}
          {update.isError && (
            <div className="mt-2 rounded-md border border-red-200 bg-red-50 p-3">
              <div className="flex items-center gap-2 text-sm font-medium text-red-800">
                <AlertCircle className="h-4 w-4 text-red-600" />
                Save failed - nothing was stored
              </div>
              <p className="mt-1 text-[11px] text-red-700">{(update.error as Error).message}</p>
              <p className="mt-1 text-[11px] text-red-700">
                If that says &quot;Network Error&quot;, the API isn&apos;t reachable - check it is
                running, then Save again.
              </p>
            </div>
          )}
          {update.isSuccess && (
            <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-[11px] text-emerald-800">
              Saved. Sensitive values stay hidden - the badge above is your confirmation.
            </div>
          )}
          {verifyResult && <VerifyResultCard result={verifyResult} channelKind={channel.kind} />}
        </div>
      )}
    </UzDrawer>
  );
};

const VerifyResultCard = ({
  result,
  channelKind,
}: {
  result: VerifyNotificationChannelResponse;
  channelKind: NotificationChannelItem['kind'];
}): React.JSX.Element => {
  const ok = result.ok;
  const stageLabel: Record<VerifyNotificationChannelResponse['stage'], string> = {
    config: 'Config check',
    handshake: 'Handshake',
    ready: 'Ready',
  };
  return (
    <div
      className={`mt-2 rounded-md border p-3 ${
        ok ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'
      }`}
    >
      <div className="flex items-center gap-2">
        {ok ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        ) : (
          <AlertCircle className="h-4 w-4 text-red-600" />
        )}
        <span className={`text-sm font-medium ${ok ? 'text-emerald-800' : 'text-red-800'}`}>
          {stageLabel[result.stage]}: {ok ? 'connected' : 'failed'}
        </span>
      </div>
      {result.host && (
        <p className="mt-1 text-xs text-zinc-600">
          <span className="font-mono">{result.host}</span>
          {result.port && <>:{result.port}</>}
          {result.from && <> as {result.from}</>}
        </p>
      )}
      {result.error && (
        <pre className="mt-2 rounded bg-white p-2 text-[11px] whitespace-pre-wrap text-red-700 ring-1 ring-red-200">
          {result.error}
        </pre>
      )}
      {/* Be honest about what a WhatsApp pass proves. DoubleTick's
          channel-profile endpoint validates the key, but it cannot know
          whether any given template is APPROVED - only a real send
          proves that. An operator who reads this as "WhatsApp works"
          without a test send could still ship a broken template. */}
      {ok && channelKind === 'WHATSAPP' && (
        <p className="mt-2 text-[11px] leading-relaxed text-emerald-800">
          The API key is valid and DoubleTick answered. This does <strong>not</strong> verify that
          any given template is approved on the WABA. Confirm with &quot;Send a real test&quot; on
          the WhatsApp template (Templates tab).
        </p>
      )}
    </div>
  );
};
